/**
 * Style3 (Feature Focus) — Style-specific sections for GridEngine.
 *
 * hero_carousel: Feature focus cards with 3 rotating icons
 * charity_banner: Custom gradient section with 50/50/100% stat cards + CTA
 */
import React, { useState, useEffect } from 'react'
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import Zap from 'lucide-react/dist/esm/icons/zap.js';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right.js';
import InlineTranslationEdit from '../../components/InlineTranslationEdit'
import HeroPartnerGlassSelect from '../../components/HeroPartnerGlassSelect'
import { useContent } from '../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../hooks/usePurchaseNavigate'

/**
 * Style3 hero carousel — "Why Play 50/50?" with 3 feature focus cards.
 * Mirrors Classic5050Style3.jsx lines 44-67 (features) + 232-330 (render).
 */
export function Style3HeroCarouselSection({ t, editMode, handleTranslationUpdate }) {
  const { c } = useContent()
  const [activeFeature, setActiveFeature] = useState(0)

  const features = [
    {
      icon: Trophy,
      title: c('classic5050.feature1Title', t?.('classic5050.feature1Title') || 'Win Big Prizes'),
      description: c('classic5050.feature1Description', t?.('classic5050.feature1Description') || 'Take home 50% of the jackpot when your number is drawn'),
    },
    {
      icon: Heart,
      title: c('classic5050.feature2Title', t?.('classic5050.feature2Title') || 'Support Community'),
      description: c('classic5050.feature2Description', t?.('classic5050.feature2Description') || 'Every ticket helps fund vital programs and services'),
    },
    {
      icon: Zap,
      title: c('classic5050.feature3Title', t?.('classic5050.feature3Title') || 'Easy & Fast'),
      description: c('classic5050.feature3Description', t?.('classic5050.feature3Description') || 'Quick checkout and instant confirmation'),
    },
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [features.length])

  return (
    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden">
      <div className="p-8 md:p-12">
        <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
          <InlineTranslationEdit
            translationKey="HomeHowItWorks"
            value={c('HomeHowItWorks', t?.('classic5050.whyPlay'))}
            fallback="Why Play 50/50?"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
            className="text-3xl font-bold"
          />
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon
            const isActive = index === activeFeature

            return (
              <div
                key={index}
                className={`relative overflow-hidden rounded-2xl p-6 cursor-pointer transition-all duration-500 border-2 ${
                  isActive
                    ? 'shadow-lg scale-105'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
                style={isActive ? { borderColor: 'color-mix(in srgb, var(--charity-primary) 55%, transparent)' } : {}}
                onClick={() => setActiveFeature(index)}
              >
                <div
                  className="rounded-xl p-4 mb-4 inline-block"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--charity-primary) 8%, transparent)' }}
                >
                  <Icon className="w-8 h-8" style={{ color: 'var(--charity-primary)' }} />
                </div>

                <h4
                  className={`text-xl font-bold mb-3 ${isActive ? '' : 'text-gray-900 dark:text-white'}`}
                  style={isActive ? { color: 'var(--charity-primary)' } : {}}
                >
                  <InlineTranslationEdit
                    translationKey={`classic5050.feature${index + 1}Title`}
                    value={feature.title}
                    fallback={feature.title}
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                    className="text-xl font-bold"
                  />
                </h4>

                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  <InlineTranslationEdit
                    translationKey={`classic5050.feature${index + 1}Description`}
                    value={feature.description}
                    fallback={feature.description}
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                    className="text-sm"
                    multiline={true}
                  />
                </p>

                {isActive && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl"
                    style={{ backgroundImage: 'linear-gradient(to right, var(--charity-primary), var(--charity-accent))' }}
                  />
                )}
              </div>
            )
          })}
        </div>

        <div className="flex justify-center gap-2 mt-8">
          {features.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === activeFeature ? 'w-6' : ''
              }`}
              style={{
                backgroundColor: index === activeFeature
                  ? 'var(--charity-primary)'
                  : 'color-mix(in srgb, var(--charity-primary) 30%, transparent)',
              }}
              onClick={() => setActiveFeature(index)}
            />
          ))}
        </div>

        <HeroPartnerGlassSelect className="mx-auto mt-8 w-full max-w-md" />
      </div>
    </div>
  )
}

/**
 * Style3 charity banner — gradient section with 50/50/100% stat cards + CTA.
 * Mirrors Classic5050Style3.jsx lines 128-229.
 */
export function Style3CharityBannerSection({ t, editMode, handleTranslationUpdate, mainDraw, onLoginClick }) {
  const { c } = useContent()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)

  return (
    <div
      className="rounded-3xl shadow-2xl overflow-hidden relative"
      style={{
        backgroundImage: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
        color: 'var(--charity-primary-contrast)',
      }}
    >
      <div className="absolute inset-0 bg-grid-white/10" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -mr-48 -mt-48" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full blur-3xl -ml-36 -mb-36" />

      <div className="relative z-10 p-8 md:p-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-6 transform hover:scale-110 transition-transform duration-300">
            <Heart className="w-10 h-10 text-white animate-pulse" />
          </div>

          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            <InlineTranslationEdit
              translationKey="HomeToutText"
              value={c('HomeToutText', t?.('classic5050.charityBannerTitle'))}
              fallback="Play with Purpose"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
              className="text-4xl md:text-5xl font-black text-white"
            />
          </h2>

          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            <InlineTranslationEdit
              translationKey="HomeParticipatingCaption"
              value={c('HomeParticipatingCaption', t?.('classic5050.charityBannerSubtitle'))}
              fallback="Every ticket purchased creates meaningful change in our community"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
              className="text-xl text-white/90"
            />
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="text-4xl font-bold text-white mb-2">50%</div>
              <div className="text-white/80 text-sm">
                <InlineTranslationEdit translationKey="classic5050.toWinner" value={c('classic5050.toWinner', t?.('classic5050.toWinner'))} fallback="To Winner" onUpdate={handleTranslationUpdate} editMode={editMode} className="text-white/80 text-sm" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="text-4xl font-bold text-white mb-2">50%</div>
              <div className="text-white/80 text-sm">
                <InlineTranslationEdit translationKey="classic5050.toCommunity" value={c('classic5050.toCommunity', t?.('classic5050.toCommunity'))} fallback="To Community" onUpdate={handleTranslationUpdate} editMode={editMode} className="text-white/80 text-sm" />
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
              <div className="text-4xl font-bold text-white mb-2">100%</div>
              <div className="text-white/80 text-sm">
                <InlineTranslationEdit translationKey="classic5050.impact" value={c('classic5050.impact', t?.('classic5050.impact'))} fallback="Impact" onUpdate={handleTranslationUpdate} editMode={editMode} className="text-white/80 text-sm" />
              </div>
            </div>
          </div>

          <button
            onClick={() => purchaseNavigate('/purchase')}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-lg shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300"
            style={{ backgroundColor: 'var(--charity-primary)', color: 'var(--charity-primary-contrast)' }}
          >
            <InlineTranslationEdit
              translationKey="HomeStandardCTAButton"
              value={c('HomeStandardCTAButton', t?.('classic5050.getTicketsNow'))}
              fallback="Get Your Tickets Now"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
              className="font-bold text-lg"
            />
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
